import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ChevronDown, 
  Banknote, 
  Building2, 
  GraduationCap, 
  Hammer, 
  Heart, 
  ShoppingBag,
  ArrowRight,
  Linkedin,
  Twitter,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Play,
  Users,
  TrendingUp
} from "lucide-react";

const LandingPage = () => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Sectors data with links
  const sectors = [
    {
      icon: <Banknote className="w-8 h-8" />,
      title: "Nhalege Capital",
      description: "Investment engine for assets, startups, and land development",
      image: "https://images.unsplash.com/photo-1507924912916-7ba10ba86757",
      link: "/capital",
      tagline: "Investing for the Future"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Nhalege Real Estate",
      description: "Wholesaling, rentals, and development - eco-retreats, apartments, hotels",
      image: "https://images.unsplash.com/photo-1570206986634-afd7cccb68d3",
      link: "/real-estate",
      tagline: "Land is Power"
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Nhalege Media & Education",
      description: "Content, branding, documentaries, and transformative online courses",
      image: "https://images.unsplash.com/photo-1620794511798-d7ba5299a087",
      link: "/media",
      tagline: "Content is Currency"
    },
    {
      icon: <Hammer className="w-8 h-8" />,
      title: "Nhalege Contracting",
      description: "Construction and property rehab for internal and external clients",
      image: "https://images.unsplash.com/photo-1489515229412-1f3a8f08dc34",
      link: "/contracting",
      tagline: "Build the Future"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Nhalege Wealth Foundation",
      description: "Land-backed wellness, youth support, and global transformation nonprofit",
      image: "https://images.pexels.com/photos/9886973/pexels-photo-9886973.jpeg",
      link: "/foundation",
      tagline: "The Nonprofit"
    },
    {
      icon: <ShoppingBag className="w-8 h-8" />,
      title: "Nhalege E-commerce",
      description: "Physical product brands focused on herbs, health, and culture",
      image: "https://images.pexels.com/photos/6809871/pexels-photo-6809871.jpeg",
      link: "/ecommerce",
      tagline: "Influence and Income"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M100 0L173.2 50v100L100 200L26.8 150V50z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px',
              backgroundPosition: 'center'
            }}
          />
        </div>

        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1573767291321-c0af2eaf5266"
            alt="Futuristic cityscape"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-yellow-900/30"></div>
        </div>

        {/* Particle Effects */}
        <motion.div 
          className="absolute inset-0 opacity-30"
          animate={{ 
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ 
            duration: 30, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/7605539/pexels-photo-7605539.jpeg')",
            backgroundSize: "cover",
            filter: "blur(2px)"
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-yellow-400/50">
              <span className="text-black font-bold text-3xl">⬟</span>
            </div>
          </motion.div>

          <motion.h1 
            className="text-6xl md:text-8xl lg:text-9xl font-bold mb-6 leading-tight"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <span className="text-white">Nhalege LLC</span>
            <br />
            <span className="text-yellow-400 text-4xl md:text-6xl lg:text-7xl">Master the Game of Life</span>
          </motion.h1>
          
          <motion.p 
            className="text-2xl md:text-3xl text-white/80 mb-4 max-w-4xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            Start Small. Think Big. Build Forever.
          </motion.p>

          <motion.p 
            className="text-xl md:text-2xl text-yellow-400/90 mb-12 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            Generational wealth through strategic rebellion against the status quo.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            <Link 
              to="#sectors"
              className="group bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-yellow-400/40 transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center space-x-2">
                <span>Explore the Sectors</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
            
            <Link 
              to="#contact"
              className="group border-2 border-yellow-400 text-yellow-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-yellow-400 hover:text-black transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Join the Movement</span>
              </div>
            </Link>
            
            <Link 
              to="#contact"
              className="group border-2 border-white/30 text-white px-12 py-4 text-lg font-bold rounded-full hover:bg-white/10 transition-all duration-300 hover:scale-105"
            >
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5" />
                <span>Invest with Us</span>
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          style={{ opacity }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-yellow-400/80" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-gray-900 to-black relative">
        {/* Sacred Geometry Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b'%3E%3Cpath d='M30 30m-28 0a28 28 0 1 1 56 0a28 28 0 1 1 -56 0' stroke='%23f59e0b' stroke-width='1' fill='none'/%3E%3Cpath d='M30 2v56M2 30h56'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-7xl font-bold mb-8"
              variants={fadeInUp}
            >
              What is <span className="text-yellow-400">Nhalege</span>?
            </motion.h2>
            
            <motion.div 
              className="max-w-4xl mx-auto space-y-8"
              variants={staggerContainer}
            >
              <motion.p 
                className="text-2xl md:text-3xl text-white/90 leading-relaxed font-light"
                variants={fadeInUp}
              >
                Nhalege means mastering knowledge to gain freedom. We reject false systems and build our own.
              </motion.p>
              
              <motion.p 
                className="text-xl md:text-2xl text-white/80 leading-relaxed"
                variants={fadeInUp}
              >
                Our vision is to lead humanity into its next phase by owning land, media, capital, and opportunity.
              </motion.p>
              
              <motion.blockquote 
                className="text-3xl md:text-4xl font-light italic text-yellow-400 border-l-4 border-yellow-400 pl-8 py-6 relative"
                variants={fadeInUp}
              >
                <div className="absolute -left-2 -top-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                  <span className="text-black text-xl">⬟</span>
                </div>
                "We're not just building businesses, we're architecting the future of generational wealth."
                <footer className="text-xl text-white/70 mt-6 not-italic">
                  — Julian Miles, Founder & CEO
                </footer>
              </motion.blockquote>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Sectors Section */}
      <section id="sectors" className="py-24 bg-black relative">
        {/* Tech Circuit Background */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1576400883215-7083980b6193"
            alt="Circuit pattern"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-8">
              Our <span className="text-yellow-400">Empire</span>
            </h2>
            <p className="text-2xl text-white/80 max-w-3xl mx-auto">
              Six interconnected divisions working in harmony to build lasting wealth and impact.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {sectors.map((sector, index) => (
              <motion.div
                key={sector.title}
                className="group relative bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-500 overflow-hidden"
                variants={fadeInUp}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Background Image */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-20 group-hover:opacity-30 transition-opacity duration-500">
                  <img 
                    src={sector.image} 
                    alt={sector.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
                
                {/* Sacred Geometry Corner */}
                <div className="absolute top-4 right-4 w-6 h-6 border border-yellow-400/30 rotate-45"></div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="text-yellow-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {sector.icon}
                  </div>
                  <div className="text-sm text-yellow-400/80 font-semibold mb-2 tracking-wider uppercase">
                    {sector.tagline}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-yellow-400 transition-colors duration-300">
                    {sector.title}
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {sector.description}
                  </p>
                  <Link 
                    to={sector.link}
                    className="inline-flex items-center text-yellow-400 font-semibold group-hover:text-white transition-colors duration-300"
                  >
                    Explore Sector
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/0 via-yellow-400/0 to-yellow-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Highlight Section */}
      <section id="vision" className="py-24 bg-gradient-to-br from-gray-900 via-black to-yellow-900/20 relative">
        {/* Particle Background */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.pexels.com/photos/9665178/pexels-photo-9665178.jpeg"
            alt="Particle system"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-6xl md:text-8xl font-bold mb-8">
              From <span className="text-yellow-400">Content</span> to <span className="text-yellow-400">Concrete</span>
            </h2>
            <p className="text-3xl text-white/80 mb-12 font-light">
              Everything We Touch Builds.
            </p>
          </motion.div>

          <motion.div 
            className="relative max-w-5xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            {/* Ecosystem Graphic */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1507220529008-e931df30d1ed"
                alt="Circular ecosystem"
                className="w-full h-96 object-cover rounded-3xl opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/70 rounded-3xl"></div>
              
              {/* Overlay Content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <motion.div
                    className="w-40 h-40 mx-auto mb-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center relative"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <div className="text-black font-bold text-2xl">UNITY</div>
                    {/* Sacred Geometry Overlay */}
                    <div className="absolute inset-2 border-2 border-black/20 rounded-full"></div>
                    <div className="absolute inset-4 border border-black/20 rounded-full"></div>
                  </motion.div>
                  <p className="text-white/90 text-xl max-w-3xl leading-relaxed">
                    Our sectors work in perfect synergy - capital flows through real estate, 
                    media educates and attracts, contracting builds the vision, 
                    foundation ensures impact, and e-commerce sustains growth.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-black relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-16">
              The <span className="text-yellow-400">Revolution</span>
            </h2>
            
            <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-yellow-400/20 rounded-3xl p-12 max-w-4xl mx-auto relative">
              {/* Sacred Geometry Corners */}
              <div className="absolute top-6 left-6 w-8 h-8 border-2 border-yellow-400/30 rotate-45"></div>
              <div className="absolute bottom-6 right-6 w-8 h-8 border-2 border-yellow-400/30 rotate-45"></div>
              
              <blockquote className="text-3xl md:text-4xl font-light italic text-white/90 mb-8">
                "Real change starts when vision meets infrastructure."
              </blockquote>
              <div className="text-yellow-400 text-xl font-semibold">
                — Industry Leader
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-yellow-400/10 via-black to-black relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-6xl md:text-8xl font-bold mb-8"
              variants={fadeInUp}
            >
              Build <span className="text-yellow-400">with Us</span>
            </motion.h2>
            
            <motion.p 
              className="text-2xl md:text-3xl text-white/80 mb-12 max-w-4xl mx-auto leading-relaxed"
              variants={fadeInUp}
            >
              Ready to be part of something that will outlast generations? 
              Join us in building the future of wealth and impact.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              variants={fadeInUp}
            >
              <Link 
                to="/capital"
                className="group bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-16 py-5 text-xl font-bold rounded-full hover:shadow-2xl hover:shadow-yellow-400/40 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-3">
                  <span>Join the Mission</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </div>
              </Link>
              
              <Link 
                to="/capital"
                className="group border-2 border-yellow-400 text-yellow-400 px-16 py-5 text-xl font-bold rounded-full hover:bg-yellow-400 hover:text-black transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-6 h-6" />
                  <span>Invest Now</span>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-yellow-400/20 relative">
        {/* Sacred Geometry Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b'%3E%3Cpath d='M50 0L93.3 25v50L50 100L6.7 75V25z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <span className="text-black font-bold text-xl">N</span>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">
                    Nhalege<span className="text-yellow-400">.</span>
                  </div>
                  <div className="text-xs text-white/60 font-medium tracking-wider uppercase">
                    Master the Game
                  </div>
                </div>
              </div>
              <p className="text-white/70 mb-6 max-w-md leading-relaxed">
                Redefining legacy through wealth, innovation, and action. 
                Building the future one sector at a time through strategic rebellion against the status quo.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-white/60 hover:text-yellow-400 transition-colors p-2 bg-white/5 rounded-lg hover:bg-yellow-400/10">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="#" className="text-white/60 hover:text-yellow-400 transition-colors p-2 bg-white/5 rounded-lg hover:bg-yellow-400/10">
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="#" className="text-white/60 hover:text-yellow-400 transition-colors p-2 bg-white/5 rounded-lg hover:bg-yellow-400/10">
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </div>
            
            {/* Navigation */}
            <div>
              <h3 className="text-white font-bold mb-4 text-lg">Empire</h3>
              <ul className="space-y-3">
                {sectors.slice(0, 3).map((sector) => (
                  <li key={sector.link}>
                    <Link 
                      to={sector.link}
                      className="text-white/60 hover:text-yellow-400 transition-colors flex items-center space-x-2"
                    >
                      {sector.icon}
                      <span>{sector.title.replace('Nhalege ', '')}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h3 className="text-white font-bold mb-4 text-lg">Connect</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-white/60">
                  <Mail className="w-4 h-4 mr-3 text-yellow-400" />
                  info@nhalege.com
                </li>
                <li className="flex items-center text-white/60">
                  <Phone className="w-4 h-4 mr-3 text-yellow-400" />
                  +1 (555) 123-4567
                </li>
                <li className="flex items-center text-white/60">
                  <MapPin className="w-4 h-4 mr-3 text-yellow-400" />
                  Global Operations
                </li>
              </ul>
              
              <div className="mt-6">
                <h4 className="text-white font-semibold mb-3">Join the Revolution</h4>
                <div className="flex">
                  <input 
                    type="email" 
                    placeholder="Your email"
                    className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-l-lg text-white placeholder-white/50 focus:outline-none focus:border-yellow-400"
                  />
                  <button className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold rounded-r-lg hover:shadow-lg hover:shadow-yellow-400/30 transition-all duration-300">
                    Join
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-white/60 text-sm">
              © 2024 Nhalege LLC. All rights reserved. | Start Small. Think Big. Build Forever.
            </p>
            <p className="text-yellow-400/60 text-sm mt-4 md:mt-0 flex items-center">
              <span className="mr-2">⬟</span>
              Powered by Aurelius – Nhalege's Internal AI Ops Assistant
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;